import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Box,
  InputAdornment,
  FormControlLabel,
  Switch,
  Chip,
  Autocomplete,
  createFilterOptions
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { FaArrowLeft, FaSearch, FaPlus } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import { cvManagementApi } from '../../../services/api';
import { Container, Header, HeaderLeft, BackButton, Title, Content, HeaderButton, ControlsContainer } from '../../common/styles';
import { MainContainer, CatalogGrid, CatalogCard, CatalogCardContent, CatalogTitle, CatalogDetails, ActionButtons } from './styles';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import { useAlert } from '../../../contexts/AlertContext';

const categoryFilter = createFilterOptions();

const CertificationCatalog = () => {
  const navigate = useNavigate();
  const { issuerId } = useParams();
  const { showAlert } = useAlert();
  const [issuer, setIssuer] = useState(null);
  const [catalogEntries, setCatalogEntries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentEntry, setCurrentEntry] = useState({ name: '', categoryId: null, expires: false });
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showControls, setShowControls] = useState(true);
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  useEffect(() => {
    fetchData();
  }, [issuerId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [issuersData, categoriesData] = await Promise.all([
        cvManagementApi.getGeneralCertificationIssuers(),
        cvManagementApi.getAllCertificationCategories()
      ]);

      const currentIssuer = issuersData.find(i => i.id === issuerId);
      if (!currentIssuer) {
        showAlert('Certification issuer not found.', 'error');
        navigate('/cv-configuration/certification-issuer');
        return;
      }

      setIssuer(currentIssuer);
      setCatalogEntries(currentIssuer.catalogCertifications || []);
      setCategories(categoriesData || []);
    } catch (err) {
      showAlert('Failed to load catalog data.', 'error', err);
      console.error('Error fetching catalog data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (entry = null) => {
    if (entry) {
      setCurrentEntry({
        id: entry.id,
        name: entry.name,
        categoryId: entry.category?.id || entry.certificationCategoryId || null,
        expires: entry.expires || false
      });
      setIsEditing(true);
    } else {
      setCurrentEntry({ name: '', categoryId: null, expires: false });
      setIsEditing(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleOpenDeleteDialog = (entry) => {
    setCurrentEntry(entry);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const payload = {
        name: currentEntry.name,
        certificationCategoryId: currentEntry.categoryId || undefined,
        expires: currentEntry.expires
      };
      if (isEditing) {
        await cvManagementApi.updateCatalogEntry(issuerId, currentEntry.id, payload);
        showAlert('Catalog certification updated successfully', 'success');
      } else {
        await cvManagementApi.createCatalogEntry(issuerId, payload);
        showAlert('Catalog certification created successfully', 'success');
      }
      handleCloseDialog();
      await fetchData();
    } catch (err) {
      showAlert(err.message || `Failed to ${isEditing ? 'update' : 'create'} catalog certification.`, 'error', err);
      console.error(`Error ${isEditing ? 'updating' : 'creating'} catalog certification:`, err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await cvManagementApi.deleteCatalogEntry(issuerId, currentEntry.id);
      showAlert('Catalog certification deleted successfully', 'success');
      handleCloseDeleteDialog();
      await fetchData();
    } catch (err) {
      showAlert(err.message || 'Failed to delete catalog certification. It may be in use.', 'error', err);
      console.error('Error deleting catalog certification:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async () => {
    try {
      const created = await cvManagementApi.createCertificationCategory({ name: newCategoryName });
      showAlert('Category created successfully', 'success');
      setCategories([...categories, created]);
      setCurrentEntry({ ...currentEntry, categoryId: created.id });
      setOpenCategoryDialog(false);
      setNewCategoryName('');
    } catch (err) {
      showAlert(err.message || 'Failed to create category.', 'error', err);
      console.error('Error creating category:', err);
    }
  };

  const filteredEntries = catalogEntries.filter(entry =>
    entry.name.toLowerCase().includes((searchTerm || '').toLowerCase())
  );

  if (loading && catalogEntries.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/cv-configuration/certification-issuer')}>
            <FaArrowLeft /> Back to Certification Issuers
          </BackButton>
          <Title>{issuer?.name ? `${issuer.name} - Certification Catalog` : 'Certification Catalog'}</Title>
        </HeaderLeft>
      </Header>

      <Content>
        <ControlsContainer $show={showControls} style={{ marginBottom: '20px' }}>
          <Box sx={{
            display: 'flex',
            gap: '16px',
            flexWrap: 'wrap',
            width: '100%',
            alignItems: 'flex-start',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap', flex: 1 }}>
              <TextField
                placeholder="Search Certifications"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch style={{ color: '#6B7280' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  maxWidth: '500px',
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    height: '40px',
                    fontSize: '14px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2563eb',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2563eb',
                    }
                  },
                  '& .MuiInputBase-input': {
                    padding: '8px 14px',
                  }
                }}
              />
            </Box>

            <HeaderButton
              onClick={() => handleOpenDialog()}
              sx={{
                minWidth: 'auto',
                height: '32px',
                padding: '0 16px',
                backgroundColor: '#2563eb',
                color: 'white',
                alignSelf: 'flex-start',
                '&:hover': {
                  backgroundColor: '#1d4ed8',
                }
              }}
            >
              <FaPlus style={{ marginRight: '8px' }} /> Add Certification
            </HeaderButton>
          </Box>
        </ControlsContainer>

        <MainContainer>
          <div style={{
            color: '#64748B',
            fontSize: '14px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <span style={{ fontWeight: 600 }}>
              {filteredEntries.length}
            </span>
            {filteredEntries.length === 1 ? 'certification' : 'certifications'} found
          </div>

          {filteredEntries.length === 0 ? (
            <EmptyState
              title="No catalog certifications found"
              description={searchTerm ?
                "No certifications match your search criteria. Try adjusting your search term." :
                `No certifications have been added to ${issuer?.name || 'this issuer'} yet. Click the '+ Add Certification' button to add your first certification.`}
            />
          ) : (
            <CatalogGrid>
              {filteredEntries.map((entry) => (
                <CatalogCard key={entry.id}>
                  <CatalogCardContent>
                    <CatalogTitle>
                      {entry.name}
                    </CatalogTitle>
                    <CatalogDetails>
                      {entry.category && (
                        <Typography variant="caption" color="text.secondary">
                          {entry.category.name}
                        </Typography>
                      )}
                      {entry.expires && (
                        <Chip
                          label="Expires"
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '11px' }}
                        />
                      )}
                    </CatalogDetails>
                    <ActionButtons>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(entry)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDeleteDialog(entry)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ActionButtons>
                  </CatalogCardContent>
                </CatalogCard>
              ))}
            </CatalogGrid>
          )}
        </MainContainer>
      </Content>

      {/* Add/Edit Catalog Certification Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? 'Edit Catalog Certification' : 'Add Catalog Certification'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Certification Name"
            type="text"
            fullWidth
            variant="outlined"
            value={currentEntry.name}
            onChange={(e) => setCurrentEntry({ ...currentEntry, name: e.target.value })}
          />
          <Autocomplete
            options={categories}
            getOptionLabel={(option) => {
              if (option.inputValue) {
                return option.inputValue;
              }
              return option.name || '';
            }}
            value={categories.find(c => c.id === currentEntry.categoryId) || null}
            onChange={(e, newValue) => {
              if (newValue && newValue.inputValue) {
                setNewCategoryName(newValue.inputValue);
                setOpenCategoryDialog(true);
              } else {
                setCurrentEntry({
                  ...currentEntry,
                  categoryId: newValue?.id || null
                });
              }
            }}
            filterOptions={(options, params) => {
              const filtered = categoryFilter(options, params);
              if (params.inputValue !== '') {
                filtered.push({
                  inputValue: params.inputValue,
                  name: `Add "${params.inputValue}"`
                });
              }
              return filtered;
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.id || 'create-new'}>
                {option.inputValue ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main', fontWeight: 500 }}>
                    <AddIcon fontSize="small" sx={{ mr: 1 }} />
                    Add &quot;{option.inputValue}&quot;
                  </Box>
                ) : (
                  option.name
                )}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Category (optional)"
                margin="dense"
                variant="outlined"
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            sx={{ mt: 1 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={currentEntry.expires || false}
                onChange={(e) => setCurrentEntry({ ...currentEntry, expires: e.target.checked })}
              />
            }
            label="This certification expires"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={!currentEntry.name.trim()}
          >
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the catalog certification "{currentEntry.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Category Dialog */}
      <Dialog open={openCategoryDialog} onClose={() => setOpenCategoryDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Certification Category</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Category Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCategoryDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCreateCategory}
            variant="contained"
            color="primary"
            disabled={!newCategoryName.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CertificationCatalog;
